/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Tooltip } from "bootstrap";

import { Images } from "./Images";
import CustomDatePicker from "../Common/CustomDatePicker";
import CustomSearchableDropdown from "../Common/CustomSearchableDropdown";
import CustomSearchableDropdownTickmark from "../Common/CustomSearchableDropdownTickmark";
import { CreateNewMeeting_api, CreateNewMeetingTimeGrid_api, GetParticipantsForMeeting_api } from "../Common/CommonAPI";
import StepProgress from "./StepProgress";
import { SpotAvailability_api } from "../NewBooking/NewBookingAPI";
import { handleApiResponse } from "../../hooks/notificationHelper";
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";
import { useApiResponseHandler } from "../../hooks/useApiResponseHandler";
import { getEmailID, getFirstname, getLastname, getLoginId } from "../../utils/SecurityService";
import { delay, formatDate, formatDateToLong, formatReactString, isPastTimeInterval, EMAIL_PATTERN } from "../../utils/CommonUtils";
import NotificationMessage from "./NotificationMessage";

const BookMeeting = ({ selectedTab, step, setStep, bookingInfo = {}, needApproval, setNeedApproval, setMeetingStep, setZoneCreditValue }) => {
  const { t, i18n } = useTranslation();

  const calendarRef = useRef(null);
  const meetingNameRef = useRef(null);

  const history = useHistory();
  const FirstName = getFirstname();
  const LastName = getLastname();
  const memberEmail = getEmailID();
  const PId = getLoginId();
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const [showTooltip, setShowTooltip] = useState(false);

  const [errors, setErrors] = useState({
    meetingName: "",
    timeRequired: "",
    timeZero: "",
    timeInvalid: "",
    endDate: "",
    recurrenceDay: ""
  });
  // 🔹 Basic State
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [activeField, setActiveField] = useState(null);
  // values: "from" | "to"

  const [selectedFrom, setSelectedFrom] = useState("");
  const [selectedTo, setSelectedTo] = useState("");
  const [maxDate, setMaxDate] = useState(null);

  const [timeSlots, setTimeSlots] = useState([]);
  const [showSlots, setShowSlots] = useState(false);
  const [timePoints, setTimePoints] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [zoneCurrentDate, setZoneCurrentDate] = useState("");
  const [zoneCurrentTime, setZoneCurrentTime] = useState("");

  // 🔹 Meeting Fields
  const [meetingName, setMeetingName] = useState("");
  const [notes, setNotes] = useState("");
  const [endDate, setEndDate] = useState(null);
  //const [recurrence, setRecurrence] = useState("");
  const [currentSection, setCurrentSection] = useState(0);

  // GuestParticipants: for create new meeting API
  const [externalGuests, setExternalGuests] = useState([]);
  const [guestInput, setGuestInput] = useState({
    name: "",
    surname: "",
    email: "",
    nationalIdentificationId: ""
  });

  const [meetingRoomData, setMeetingRoomData] = useState(null);

  // Participants: for create new meeting API (initial sample entries moved into state so we can append/remove)
  const [participants, setParticipants] = useState([]);
  const [checkedParticipants, setCheckedParticipants] = useState([]);
  const [showGuestForm, setShowGuestForm] = useState(false);

  const [repeatType, setRepeatType] = useState(0);
  const [weeklyOptions, setWeeklyOptions] = useState([]);
  const [monthlyOption, setMonthlyOption] = useState(null);

  const [repeatOptionsList, setRepeatOptionsList] = useState([]);
  const [weeklyOptionsList, setWeeklyOptionsList] = useState([]);
  const [monthlyOptionsList, setMonthlyOptionsList] = useState([]);

  const [isRepeatEnabled, setIsRepeatEnabled] = useState(false);

  // 🔹 Selected Zone Object
  const selectedZoneObj = useMemo(() => {
    return bookingInfo?.ZoneList?.find(
      z => String(z.Id) === String(selectedZone)
    );
  }, [selectedZone, bookingInfo]);

  const spotTagList = useMemo(() => {
    if (!selectedZoneObj) return [];

    return selectedZoneObj.Tags || [];
  }, [selectedZoneObj]);

  const toggleTag = (tagId) => {
    setSelectedTags((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }

      return [...prev, tagId];
    });
  };

  const zoneCreditValue = Number(selectedZoneObj?.ZoneCreditValue ?? 0);
  useEffect(() => {
    if (setZoneCreditValue) {
      setZoneCreditValue(zoneCreditValue);
    }
  }, [zoneCreditValue]);

  const [isFullyDayBlocked, setIsFullyDayBlocked] = useState(0);
  const [isRoomHasApprover, setIsRoomHasApprover] = useState(0);
  const isDisableOnSpotApi = isFullyDayBlocked === 1 || (isRoomHasApprover === 1 && isFullyDayBlocked === 1);

  const [timeSelectionSource, setTimeSelectionSource] = useState(null);
  // values: "step1" | "step2" | null

  const [capacity, setCapacity] = useState(0);

  const displaySelectedDate =
    selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? `${String(selectedDate.getDate()).padStart(2, "0")} ${t(
        selectedDate.toLocaleString("en-GB", { month: "long" })
      )} ${selectedDate.getFullYear()}`
      : "";

  const { handleResponseCode, showAlertPopupModal, alertPopupModalData, showErrorPopupModal,
    errorPopupModalData, setShowAlertPopupModal, setShowErrorPopupModal } = useApiResponseHandler();
  const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);
  // #region Handle Errors- start

  const meetingNamePattern = useMemo(() => {
    const raw = t("react_meeting_name_regex");
    // i18next returns the key itself when missing — guard against that.
    if (raw && raw !== "react_meeting_name_regex") {
      try {
        return new RegExp(raw);
      } catch {
        // malformed pattern in a locale file → fall through to default
      }
    }
    return /^(?!.*÷)[0-9a-zA-ZÀ-ÿĀ-ſ &,?\\'.()’-]+$/;
  }, [t, i18n.language]);

  const validateSectionOne = () => {
    let newErrors = {
      meetingName: "",
      timeRequired: "",
      timeZero: "",
      timeInvalid: "",
      endDate: "",
      recurrenceDay: "",
      firstname: "",
      lastname: "",
      email: ""
    };

    let isValid = true;

    // 1️⃣ Meeting Name
    if (!meetingName || meetingName.trim() === "") {
      newErrors.meetingName = t("react_meeting_name_is_required");
      isValid = false;
    } else if (!meetingNamePattern.test(meetingName.trim())) {
      newErrors.meetingName = t("only_alphanumeric_characters_allowed");
      isValid = false;
    } else if (meetingName.length > 30) {
      newErrors.meetingName = t("meeting_name_maximum_length_is_30_characters");
      isValid = false;
    }

    console.log("Validating times", { selectedFrom, selectedTo });

    // 2️⃣ Only FROM selected
    if (selectedFrom && !selectedTo) {
      newErrors.timeInvalid = t("react_select_end_time");
      isValid = false;
    }

    // 3️⃣ Only TO selected
    else if (!selectedFrom && selectedTo) {
      newErrors.timeInvalid = t("react_select_start_time");
      isValid = false;
    }

    // 4️⃣ Both selected → validate range
    else if (
      selectedFrom &&
      selectedTo &&
      selectedFrom !== "00:00" &&
      selectedTo !== "00:00"
    ) {
      if (selectedFrom >= selectedTo) {
        newErrors.timeInvalid = t("end_time_must_be_after_start_time");
        isValid = false;
      }
    }

    // 5️⃣ Same time (extra safety)
    if (currentSection === 1 && selectedFrom === selectedTo) {
      newErrors.timeInvalid = t("please_select_time");
      isValid = false;
    }

    if (repeatType !== 0 && isRepeatEnabled && !endDate) {
      newErrors.endDate = t("please_select_end_date");
      isValid = false;
    }

    if ((repeatType === 2 || repeatType === 4) && weeklyOptions.length === 0) {
      newErrors.recurrenceDay = t("please_select_minimum_one_day");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // #endregion Handle Errors- end

  const isTimeSelected =
    selectedFrom !== "00:00" &&
    selectedTo !== "00:00";

  // Date changes still reset the booking flow broadly.
  useEffect(() => {
    setSelectedFrom("");
    setSelectedTo("");
    setSelectedTags([]);
    setCurrentSection(0);
    setShowSlots(false);
    setNotes("");
    setEmployee("");

    setGuestInput({ name: "", surname: "", email: "" });
    setParticipants([]);
    setSelectedEmployees([]);
    setExternalGuests([]); // clear previous external guests
    setCurrentSection(0);

    setRepeatType(0);
    setEndDate(null);
  }, [selectedDate]);

  // Zone changes preserve the general meeting form values while
  // refreshing the zone-dependent selection state.
  useEffect(() => {
    setSelectedTags([]);
    setShowSlots(false);
    setActiveField(null);
    setEmployee("");
    setSelectedSpot(0);
    setSpotList([]);
    setErrors((prev) => ({
      ...prev,
      timeInvalid: "",
      capacity: ""
    }));
  }, [selectedZone]);


  // #region Interval-Time slot click handler- start
  const handleTimeClick = (time) => {
    setTimeSelectionSource("step1");

    // 👉 FROM clicked
    if (activeField === "from") {
      setSelectedFrom(time);

      // ❌ invalid
      if (selectedTo && time >= selectedTo) {
        setErrors((prev) => ({
          ...prev,
          timeInvalid: t("the_starttime_cant_before")
        }));
      } else {
        // ✅ clear error
        setErrors((prev) => ({
          ...prev,
          timeInvalid: ""
        }));
      }

      setShowSlots(false);
      return;
    }

    // 👉 TO clicked
    if (activeField === "to") {
      // ❌ no FROM selected
      if (!selectedFrom) {
        setErrors((prev) => ({
          ...prev,
          timeInvalid: t("react_please_select_from_time_first")
        }));
        return;
      }

      // ❌ invalid range
      if (time <= selectedFrom) {
        setErrors((prev) => ({
          ...prev,
          timeInvalid: t("end_time_must_be_after_start_time")
        }));
        return;
      }

      // ✅ valid → CLEAR ERROR HERE (missing part)
      setErrors((prev) => ({
        ...prev,
        timeInvalid: ""
      }));

      setSelectedTo(time);
      setShowSlots(false);
      return;
    }
  };

  useEffect(() => {
    setTimeSelectionSource(null);
  }, [selectedZone, selectedDate]);

  useEffect(() => {
    setShowSlots(false);
  }, [step]);

  const toMinutes = (time) => {
    const [hours, minutes] = String(time || "00:00").split(":").map(Number);
    return (hours * 60) + minutes;
  };

  const normalizeMeetingSlot = (slot) => String(slot || "").replace(/\s/g, "");

  const isStep2MeetingSlotDisabled = (slot) => {
    const isPast = isPastTimeInterval(
      slot,
      zoneCurrentTime,
      selectedDate,
      zoneCurrentDate
    );
    const bookedSlots = (selectedSpotObj?.BookedSlots || []).map(normalizeMeetingSlot);
    const blockedSlots = (selectedSpotObj?.BlockedBookingTime || []).map(normalizeMeetingSlot);
    const currentSlot = normalizeMeetingSlot(slot);

    return isPast || bookedSlots.includes(currentSlot) || blockedSlots.includes(currentSlot);
  };

  const isMeetingSlotWithinSelectedRange = (start, end) => {
    if (!selectedFrom || !selectedTo) return false;
    if (selectedFrom === "00:00" || selectedTo === "00:00") return false;

    return (
      toMinutes(start) >= toMinutes(selectedFrom) &&
      toMinutes(end) <= toMinutes(selectedTo)
    );
  };

  const handleStep2MeetingSlotClick = (slot) => {
    if (isStep2MeetingSlotDisabled(slot)) return;

    const clickedIndex = timeSlots.indexOf(slot);
    if (clickedIndex === -1) return;

    const selectedIndexes = timeSlots.reduce((indexes, currentSlot, index) => {
      const [slotStart, slotEnd] = currentSlot.split("-");

      if (isMeetingSlotWithinSelectedRange(slotStart, slotEnd)) {
        indexes.push(index);
      }

      return indexes;
    }, []);

    if (selectedIndexes.length === 0) {
      const [start, end] = slot.split("-");
      setSelectedFrom(start);
      setSelectedTo(end);
      setTimeSelectionSource("step2");
      setShowSlots(true);
      setErrors((prev) => ({ ...prev, timeInvalid: "" }));
      return;
    }

    const firstIndex = selectedIndexes[0];
    const lastIndex = selectedIndexes[selectedIndexes.length - 1];

    if (clickedIndex < firstIndex) {
      let nextStartIndex = firstIndex;

      for (let index = firstIndex - 1; index >= clickedIndex; index -= 1) {
        if (isStep2MeetingSlotDisabled(timeSlots[index])) {
          break;
        }
        nextStartIndex = index;
      }

      const [start] = timeSlots[nextStartIndex].split("-");
      setSelectedFrom(start);
    } else if (clickedIndex > lastIndex) {
      let nextEndIndex = lastIndex;

      for (let index = lastIndex + 1; index <= clickedIndex; index += 1) {
        if (isStep2MeetingSlotDisabled(timeSlots[index])) {
          break;
        }
        nextEndIndex = index;
      }

      const [, end] = timeSlots[nextEndIndex].split("-");
      setSelectedTo(end);
    } else if (clickedIndex === firstIndex && clickedIndex === lastIndex) {
      setSelectedFrom("");
      setSelectedTo("");
    } else if (clickedIndex === firstIndex) {
      const [, end] = timeSlots[firstIndex].split("-");
      const [start] = timeSlots[firstIndex].split("-");
      setSelectedFrom(start);
      setSelectedTo(end);
    } else if (clickedIndex === lastIndex) {
      const [, end] = timeSlots[lastIndex - 1].split("-");
      setSelectedTo(end);
    } else {
      // In step 2, clicking a selected slot inside the range should keep
      // earlier consecutive slots selected, including the clicked slot,
      // and unselect any slots after it.
      const [, end] = timeSlots[clickedIndex].split("-");
      setSelectedTo(end);
    }

    setTimeSelectionSource("step2");
    setShowSlots(true);
    setErrors((prev) => ({ ...prev, timeInvalid: "" }));
  };

  // #endregion Interval-Time slot click handler- end

  // #region Fetch available spots when step 2 is active- start
  const [spotList, setSpotList] = useState([]);
  const [spotsLoading, setSpotsLoading] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState(0);
  const selectedSpotObj = useMemo(() => {
    return spotList.find(
      s => String(s.Id) === String(selectedSpot)
    );
  }, [selectedSpot, spotList]);

  // Step 1 uses zone intervals, while step 2 uses the selected room's
  // API-provided TimeInterval values without changing disabled-slot behavior.
  useEffect(() => {
    if (!selectedZoneObj) return;

    const intervals = selectedZoneObj.TimeInterval || [];

    setTimePoints(intervals);

    if (step === 1) {
      const step2Intervals = (selectedSpotObj?.TimeInterval || []).map((slot) => {
        const [from = "", to = ""] = String(slot).split("-");
        return `${from.trim()}-${to.trim()}`;
      });
      setTimeSlots(step2Intervals);
      return;
    }

    const slots = [];
    for (let i = 0; i < intervals.length - 1; i++) {
      slots.push(`${intervals[i]}-${intervals[i + 1]}`);
    }

    setTimeSlots(slots);
  }, [selectedZoneObj, selectedSpotObj, step]);

  useEffect(() => {
    if (!selectedSpotObj) return;

    // ✅ Set basic values
    setIsRoomHasApprover(selectedSpotObj.IsRoomHasApprover || 0);
    setIsFullyDayBlocked(selectedSpotObj.IsFullyDayBlocked || 0);
    setCapacity(selectedSpotObj.Capacity || 0);

    // ✅ Approver logic
    const approvalRequired =
      Number(selectedSpotObj?.IsRoomHasApprover) === 1 &&
      Number(selectedSpotObj?.IsUserApprover) === 0;

    setNeedApproval(approvalRequired);

    if (approvalRequired) {
      const approvalText = selectedSpotObj.ApprovalInfo || "";

      setApprovalInfo(approvalText);
      setApproverList(selectedSpotObj.ApproverList || []);

      const parsed = parseApprovalInfo(approvalText);
      setRequestWindow(parsed.request);
      setReleaseWindow(parsed.release);
    } else {
      setApprovalInfo("");
      setApproverList([]);
      setRequestWindow({ hours: "", minutes: "" });
      setReleaseWindow({ hours: "", minutes: "" });
    }

  }, [selectedSpotObj]);

  const [repeatOptionByLiftRestriction, setRepeatOptionByLiftRestriction] = useState(0);

  const fetchSpots = async () => {

    if (!selectedZone) return;

    try {

      setSpotsLoading(true);

      const params = {
        BookingDate: formatDate(selectedDate),
        ZoneId: selectedZone,
        SelectedTab: selectedTab,   // 1 for parking, 2 for desking

        FilterByStartTime: selectedFrom === "00:00" ? "" : selectedFrom,
        FilterByEndTime: selectedTo === "00:00" ? "" : selectedTo,

        FilterByTags: selectedTags.join(","),

        FilterByBookingPeriod: 3,

        VehicleSizeId: 0,
        AccessibleId: 0,
        ShareableId: 0,
        FuelId: 0,
      };

      const response = await SpotAvailability_api(params);

      /* handleApiResponse(response, setNotification, t, {
        successMsg: 0,
        errorMsg: 1
      }); */
      handleApiUiResponse(response, {
        successToaster: 0,
        errorToaster: 1,
        alertPopup: 1
      });
      if (response?.Data?.ResponseCode !== 200) {
        setStep(0);
        return;
      }
      const records = response?.Data?.Records || {};
      setZoneCurrentDate(records.ZoneCurrentDate || "");
      setZoneCurrentTime(records.ZoneCurrentTime || "");

      const meetingSpots = records.AvailableMeetingRooms || [];

      const spots =
        meetingSpots.length > 0
          ? meetingSpots
          : [];

      setSpotList(spots);

    } catch (error) {

      console.error("Spot API error", error);

    } finally {

      setSpotsLoading(false);
    }

  };

  useEffect(() => {

    if (step === 1) {
      fetchSpots();
    }

  }, [step, selectedTags, selectedZone, selectedDate]);

  useEffect(() => {
    if (spotList.length > 0) {
      const preservedSpot = spotList.find(
        (spot) => String(spot.Id) === String(selectedSpot)
      );
      const nextSpot = preservedSpot || spotList[0];

      setSelectedSpot(nextSpot.Id);

      // OPTIONAL (can remove if using selectedSpotObj effect)
      setCapacity(nextSpot.Capacity || 0);
    } else {
      setSelectedSpot(0); // reset if no spots
    }
  }, [spotList, selectedSpot]);

  // incase of Spot changes- reset time selection + related errors (but keep zone/date as is for user convenience)
  useEffect(() => {
    if (selectedSpot && timeSelectionSource !== "step1") {
      setSelectedFrom("");
      setSelectedTo("");
      setTimeSelectionSource(null);
      setShowSlots(false);
    }
  }, [selectedSpot]);
  // #endregion Fetch available spots when step 2 is active- end

  useEffect(() => {
    const basicInfo =
      bookingInfo?.MeetingRoomBookingBasicInfo;

    if (basicInfo) {
      setRepeatOptionsList(basicInfo.RepeatOption || []);
      setWeeklyOptionsList(basicInfo.WeeklyRepeatOption || []);
      setMonthlyOptionsList(basicInfo.MonthlyRepeatOption || []);
    }
  }, [bookingInfo]);

  useEffect(() => {
    const zones = bookingInfo?.ZoneList || [];

    if (zones.length > 0) {
      setSelectedZone(zones[0].Id);
    }
  }, [bookingInfo]);

  useEffect(() => {
    if (repeatType === 3 && !monthlyOption && monthlyOptionsList.length > 0) {
      setMonthlyOption(Number(monthlyOptionsList[0].Value));
    }
  }, [repeatType, monthlyOption, monthlyOptionsList]);

  useEffect(() => {
    setStep(0);
  }, [selectedTab]);

  const tooltipRef = useRef();
  useEffect(() => {
    if (tooltipRef.current) {
      new Tooltip(tooltipRef.current);
    }
  }, []);

  // #region Add Participant - start

  const [employee, setEmployee] = useState("");
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  //const selectedEmployeeIds = PId + "," + selectedEmployees.map(e => e.EmployeeId).join(","); //  "Participants": for create new meeting API
  const selectedEmployeeIds =
    selectedEmployees && selectedEmployees.length > 0
      ? PId + "," + selectedEmployees.map(e => e.EmployeeId).join(",")
      : PId;
  const [checkedEmployees, setCheckedEmployees] = useState([]);
  const totalParticipants = 1 + selectedEmployees.length + externalGuests.length;

  const employeeOptions = employeeList
    ?.filter(
      (emp) =>
        !selectedEmployees.some(
          (sel) => String(sel.EmployeeId) === String(emp.EmployeeId)
        )
    )
    .map((emp) => ({
      value: String(emp.EmployeeId),
      label: emp.EmployeeEmail
        ? `${emp.EmployeeName} (${emp.EmployeeEmail})`
        : emp.EmployeeName,
    }));

  // add employee object to selectedEmployees (id from select)
  const addEmployeeById = (empId) => {
    if (!empId) return;

    const nextCount = totalParticipants + 1;

    if (capacity > 0 && nextCount > capacity) {
      setErrors((prev) => ({
        ...prev,
        capacity: t("react_maximum_capacity_reached")
      }));
      return;
    }

    const empObj = employeeList.find(
      (x) => String(x.EmployeeId) === String(empId)
    );

    if (!empObj) return;

    setSelectedEmployees((prev) => {
      if (prev.some((s) => s.EmployeeId === empObj.EmployeeId)) return prev;
      return [...prev, empObj];
    });

    setEmployee("");
  };

  const handleCheckboxChange = (empId) => {
    setCheckedEmployees((prev) =>
      prev.includes(empId)
        ? prev.filter((id) => id !== empId) // uncheck
        : [...prev, empId]                  // check
    );
  };
  const fetchEmployeesBySpotId = async (selectedSpot) => {
    if (!selectedSpot) return;
    try {
      //setIsLoading(true);
      const reqParams =
      {
        SpotId: selectedSpot,
        Search: "",
        SelectedParticipant: "",
      };

      const response = await GetParticipantsForMeeting_api(reqParams);
      console.log("Requesting participants with params:", reqParams);
      console.log("GetParticipantsForMeeting API response:", response);
      const participantList = response?.Data?.Participants || response?.Data?.Records?.Participants || [];

      const normalized = participantList.map((e) => ({
        EmployeeId: String(e.Id ?? e.EmployeeId ?? ""),
        EmployeeName: `${e.FirstName ?? ""} ${e.LastName ?? ""}`.trim(),
        EmployeeEmail: e.PrimaryEmail ?? e.Email ?? "",
      }));

      setEmployeeList(normalized);
    } catch (err) {
      console.error("Error fetching Employees:", err);
      setEmployeeList([]);
    }
    finally {
      //setIsLoading(false);
    }
  };


  useEffect(() => {
    if (selectedSpot) {
      fetchEmployeesBySpotId(selectedSpot);
    }
    else {
      setEmployeeList([]);
    }
  }, [selectedSpot, selectedZone]);

  useEffect(() => {
    setSelectedEmployees((prev) =>
      prev.filter((selectedEmployee) =>
        employeeList.some(
          (employee) => String(employee.EmployeeId) === String(selectedEmployee.EmployeeId)
        )
      )
    );

    setCheckedEmployees((prev) =>
      prev.filter((employeeId) =>
        employeeList.some((employee) => String(employee.EmployeeId) === String(employeeId))
      )
    );
  }, [employeeList]);
  const isCapacityReached = () => {
    return capacity > 0 && totalParticipants >= capacity;
  };


  useEffect(() => {
    if (capacity <= 0 || totalParticipants < capacity) {
      setErrors((prev) => ({
        ...prev,
        capacity: ""
      }));
    }
  }, [selectedEmployees, externalGuests, capacity, totalParticipants]);

  // #endregion Add Participant - end

  // #region Approver- start
  const [approvalInfo, setApprovalInfo] = useState("");
  const [approverList, setApproverList] = useState([]);
  const [requestWindow, setRequestWindow] = useState({ hours: "", minutes: "" });
  const [releaseWindow, setReleaseWindow] = useState({ hours: "", minutes: "" });
  const parseApprovalInfo = (text = "") => {
    const numericParts = String(text).match(/\d+/g) || [];

    return {
      request: {
        hours: numericParts[0] || "00",
        minutes: numericParts[1] || "00"
      },
      release: {
        hours: numericParts[2] || "00",
        minutes: numericParts[3] || "00"
      }
    };
  };

  useEffect(() => {
    if (currentSection === 4 && !needApproval) {
      setCurrentSection(3); // fallback
    }
  }, [currentSection, needApproval]);
  // #endregion Approver- end

  // #region Handlers- start

  const handleNext = () => {
    if (currentSection === 0 || currentSection < 3) {

      const isValid = validateSectionOne();

      if (!isValid) {
        // 🔥 Focus + Scroll ONLY when meeting name error
        if (!meetingName || meetingName.trim() === "") {

          setTimeout(() => {
            const input = meetingNameRef.current;

            if (input) {
              input.focus();

              const modalBody = document.querySelector("#newmeetingsModal .modal-body");

              if (modalBody) {
                modalBody.scrollTo({
                  top: input.offsetTop - 120,
                  behavior: "smooth"
                });
              }
            }
          }, 100);
        }

        return;
      }
      if (spotList.length > 0 && timeSelectionSource !== "step1" && currentSection === 1
        && (selectedFrom === "00:00" || selectedTo === "00:00" || selectedFrom === "" || selectedTo === "")) {
        const isValid = validateSectionOne();

        if (!isValid) { return; }

      }
      // ✅ Normal flow
      if (currentSection < 3) {
        if (currentSection === 0) {
          setSelectedSpot(0);
          setSpotList([]);
          setSpotsLoading(true);
        }

        setCurrentSection((prev) => prev + 1);
        setStep(prev => prev + 1);
        setTimeout(() => {
          const modalBody = document.querySelector("#newmeetingsModal .modal-body");
          if (modalBody) modalBody.scrollTop = 0;
        }, 100);
      }
    }
  };

  const handleBack = () => {
    if (currentSection > 0) {
      if (currentSection === 1 && timeSelectionSource === "step2") {
        setSelectedFrom("00:00");
        setSelectedTo("00:00");
        setTimeSelectionSource(null);
      }

      setCurrentSection((prev) => prev - 1);

      setStep(prev => prev - 1);
    }
  };

  // "GuestParticipants" handler for create new meeting API

  const emailPattern = EMAIL_PATTERN;
  const handleConfirmExternalGuest = (e) => {
    e?.preventDefault();

    // #region Validation for Guest Input - start
    let newErrors = {
      firstname: "",
      lastname: "",
      email: ""
    };

    let isValid = true;

    if (!(guestInput.name || "").trim()) {
      newErrors.firstname = t("please_enter_valid_firstname");
      isValid = false;
    }

    if (!(guestInput.surname || "").trim()) {
      newErrors.lastname = t("enter_lastname");
      isValid = false;
    }

    const email = (guestInput.email || "").trim();

    if (!email || !emailPattern.test(email)) {
      newErrors.email = t("enter_your_valid_email_address");
      isValid = false;
    }

    setErrors(newErrors);
    //return isValid;

    // #endregion Validation for Guest Input - end
    if (isValid) {
      const nextCount = totalParticipants + 1;
      if (capacity > 0 && nextCount > capacity) {
        setErrors((prev) => ({
          ...prev,
          capacity: t("react_maximum_capacity_reached")
        }));
        return;
      }

      const { name, surname, email, nationalIdentificationId } = guestInput;

      if (!(name || "").trim() || !(email || "").trim()) {
        return;
      }

      const guestKey = `guest-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      const newGuest = {
        GuestKey: guestKey,
        FirstName: name.trim(),
        LastName: surname.trim(),
        PrimaryEmail: email.trim(),
        Id: "0",
        NationalIdentificationId: (nationalIdentificationId || "").trim()
      };

      // append guest to externalGuests array (allow multiple)
      setExternalGuests((prev) => [...(Array.isArray(prev) ? prev : []), newGuest]);

      // add a UI participant object with a unique id (avoid key collisions when Id === "0")
      const participantForUI = {
        id: guestKey,
        name: `${newGuest.FirstName} ${newGuest.LastName}`.trim(),
        email: newGuest.PrimaryEmail || "",
      };
      setParticipants((prev) => [...(Array.isArray(prev) ? prev : []), participantForUI]);

      // Clear inputs
      setGuestInput({ name: "", surname: "", email: "", nationalIdentificationId: "" });

      // Collapse the guest input section
      const collapseEl = document.getElementById("collapseExample");
      if (collapseEl) {
        collapseEl.classList.remove("show");
        collapseEl.setAttribute("aria-expanded", "false");
      }
      console.log("✅ External guest added:", newGuest);
    }
  };

  const handleRemoveParticipant = (id) => {
    if (!checkedParticipants.includes(id)) return;

    setParticipants((prev) => prev.filter((p) => p.id !== id));
    setExternalGuests((prev) =>
      prev.filter((guest) => guest.GuestKey !== id)
    );

    setCheckedParticipants((prev) =>
      prev.filter((pid) => pid !== id)
    );
    setErrors((prev) => ({
      ...prev,
      capacity: ""
    }));
  };


  const handleParticipantCheckboxChange = (id) => {
    setCheckedParticipants((prev) =>
      prev.includes(id)
        ? prev.filter((pid) => pid !== id) // uncheck
        : [...prev, id]                    // check
    );
  };

  const handleRemoveEmployee = (empId) => {
    if (!checkedEmployees.includes(empId)) return;
    if (!checkedEmployees.includes(empId)) return;

    setSelectedEmployees((prev) => {
      const updated = prev.filter((e) => e.EmployeeId !== empId);

      return updated;
    });

    setCheckedEmployees((prev) =>
      prev.filter((id) => id !== empId)
    );
    setErrors((prev) => ({
      ...prev,
      capacity: ""
    }));
  };

  const handleConfirm = async () => {
    try {
      console.log("needApproval from handleconfirm-", needApproval)
      setIsLoading(true);
      // Section 4 → just close
      if (currentSection === 4) {
        //setTimeout(() => onClose(), 600); // close modal
        setIsLoading(true);
        //onClose();
        history.push("/booking");
        return;                           // stop here
      }
      // onClose()
      console.log("repeatType, weeklyOptions, monthlyOption --> ", repeatType, weeklyOptions, monthlyOption);
      const reqParams = {
        //BookingDate: parsedDate          ? formatDate(parsedDate)          : formatDate(new Date()),
        BookingDate: selectedDate ? formatDate(selectedDate) : formatDate(new Date()),
        EndDate: endDate ? formatDate(endDate) : "",
        MeetingName: meetingName,
        SpotId: selectedSpot,
        StartTime: selectedFrom,
        EndTime: selectedTo,
        RepeatOption: repeatType || 0,
        WeeklyRepeatOption:
          Array.isArray(weeklyOptions) && weeklyOptions.length > 0
            ? weeklyOptions.join(",")
            : "0",

        MonthlyRepeatOption: monthlyOption || 0,
        Participants: selectedEmployeeIds,
        Note: notes,
        EndDateBy: 1,
        GuestParticipants: externalGuests,
      };
      console.log("Event Parameters:", reqParams);
      const data = await CreateNewMeeting_api(reqParams);

      console.log("✅ Create Meeting API Response:", data);
      /* handleApiResponse(data, setNotification, t, {
        successMsg: 1,
        errorMsg: 1
      }); */
      handleApiUiResponse(data, {
        successToaster: 1,
        errorToaster: 1,
        alertPopup: 1
      });
      if (data?.Data?.ResponseCode === 200) {

        //alert("✅ Meeting created successfully!");

        //if (typeof onMeetingCreated === "function") {
        console.log("✅ onMeetingCreated go inside function check");
        //onMeetingCreated();
        setEndDate(null);
        //}
        //setTimeout(() => onClose(), 600); // close modal
        console.log("needApproval-", needApproval, "currentSection-", currentSection);
        if (needApproval && currentSection < 4) {
          setIsLoading(false);
          setTimeout(() => setCurrentSection(4), 200);  // Approval required → go to section 4
        }
        else {
          await delay(1000);
          setIsLoading(true);
          setTimeout(() =>
            history.push("/booking")
            , 200);

          // setTimeout(() => onClose(), 600); // close modal
        }
      }
      else {
        //alert((data?.Data?.ErrorMessage || "Something went wrong") + "\nselectedFrom = " + selectedFrom + "\nselectedTo = " + selectedTo);
        setIsLoading(false);

        console.log("Error occured!", data?.Data?.ErrorMessage);
      }
    } catch (error) {
      console.error("❌ Error creating meeting:", error);
      //alert("Error occurred while creating meeting.");
      setNotification({
        type: "error",
        title: t("fail"),
        message: error?.message || t("oops_something_went_wrong_try_again")
      });

    } finally {
      setIsLoading(false);
      //onClose();
    }
  };
  const displayStep = currentSection >= 2 ? 2 : currentSection;
  const isStep2NoSpaceAvailable =
    currentSection === 1 &&
    !spotsLoading &&
    spotList.length === 0;
  const isStep2NextDisabled =
    currentSection === 1 &&
    (spotsLoading || !selectedSpot || isStep2NoSpaceAvailable);
  useEffect(() => {
    setMeetingStep(currentSection);
  }, [currentSection]);

  // #endregion Handlers- end 
  return (
    <div className="tab-content active">
      <StepProgress step={displayStep} activeTab="meeting" />

      {/* ================= STEP 1 ================= */}
      {(step === 0 && currentSection === 0) && (
        <div className="step active" data-step="0">

          <form>

            {/* Zone */}
            <div className="mb-4">
              <label className="popup-inner-title mt-0">{t("zone")}</label>
              <CustomSearchableDropdown
                items={bookingInfo?.ZoneList || []}
                value={selectedZone}
                labelKey="ZoneName"
                valueKey="Id"
                searchable={false}
                placeholder={t("select_a_zone")}
                onChange={(val) => setSelectedZone(val)}
                noOptionsMessage={t("no_zone_available")}
              />
            </div>

            {/* Date */}
            <div className="date-picker-wrapper mb-4">
              <label className="popup-inner-title">{t("date")}</label>
              <CustomDatePicker
                selected={selectedDate}
                onChange={(d) => setSelectedDate(d || new Date())}
                minDate={new Date()}
                onKeyDown={(e) => e.preventDefault()}
                onChangeRaw={(e) => e.preventDefault()}
              />
            </div>

            {/* Time Slots */}
            <div className="custom-timepicker mb-4">
              <label className="popup-inner-title">{t("react_select_time")}</label>

              <div
                /* className={`selected-box bg-white ${errors.timeInvalid
                  ? "error-form"
                  : selectedFrom &&
                    selectedTo &&
                    !(selectedFrom === "00:00" && selectedTo === "00:00")
                    ? "ctm-success"
                    : ""
                  }`} */
                className={`selected-box bg-white ${selectedFrom && selectedTo && !(selectedFrom === "00:00" && selectedTo === "00:00")
                  ? "ctm-success"
                  : ""
                  }`}

              >
                <div
                  onClick={() => {
                    setActiveField("from");   // ✅ track FROM
                    setShowSlots(true);
                    setErrors((prev) => ({ ...prev, timeInvalid: "" }));
                  }}
                >
                  {t("from")} <span className={!selectedFrom || selectedFrom === "00:00" ? "text-grey" : ""}>{selectedFrom || "00:00"}</span>
                </div>

                <div className="text-center">
                  <img src={Images.RightBlackArrow} className="img-fluid" />
                </div>

                <div
                  onClick={() => {
                    setActiveField("to");     // ✅ track TO
                    setShowSlots(true);
                    setErrors((prev) => ({ ...prev, timeInvalid: "" }));
                  }}
                >
                  {t("react_to")} <span className={!selectedTo || selectedTo === "00:00" ? "text-grey" : ""}>{selectedTo || "00:00"}</span>
                </div>
              </div>


              {errors.timeInvalid && (

                <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.timeInvalid}</p></div>
              )}

              {showSlots && (
                <div className="slot-container d-grid mb-3">
                  {timePoints.map((time, idx) => {
                    const hasFromValue = selectedFrom && selectedFrom !== "00:00";
                    const hasToValue = selectedTo && selectedTo !== "00:00";
                    const pointMinutes = toMinutes(time);
                    const fromMinutes = hasFromValue ? toMinutes(selectedFrom) : null;
                    const toMinutesValue = hasToValue ? toMinutes(selectedTo) : null;

                    const isSelected =
                      hasFromValue &&
                      (
                        time === selectedFrom ||
                        (hasToValue && pointMinutes >= fromMinutes && pointMinutes <= toMinutesValue)
                      );

                    return (
                      <div
                        key={idx}
                        className={`slot ${isSelected ? "active" : ""}`}
                        onClick={() => handleTimeClick(time)}
                      >
                        {time}
                      </div>
                    );
                  })}
                </div>
              )}


            </div>

            {/* 🔥 Meeting Name */}
            <div className="form-row mb-4">
              <label className="popup-inner-title m-0">{t("meeting_name")}</label>
              <input
                type="text"
                ref={meetingNameRef}
                className={`form-control 
                ${errors.meetingName ? "error-form" : ""}
                ${!errors.meetingName && meetingName ? "success-validation" : ""}
                `}
                value={meetingName}
                onChange={(e) => {
                  setMeetingName(e.target.value);
                  setErrors((prev) => ({ ...prev, meetingName: "" }));
                }}
              />


              {errors.meetingName && (

                <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.meetingName}</p></div>
              )}
            </div>


            {spotTagList?.length > 0 && (
              <div className="mb-4">
                <label className="popup-inner-title mt-0">{t("tags")}</label>
                <div className="inline-btn-tags tags-btn d-flex flex-wrap">
                  {spotTagList.map((tag) => (
                    <button
                      key={tag.Id}
                      type="button"
                      className={selectedTags.includes(tag.Id) ? "selected" : ""}
                      onClick={() => toggleTag(tag.Id)}
                    >
                      {tag.Name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 🔥 Recurrence */}
            <div className="mb-4">
              <label className="popup-inner-title">{t("recurrence")}</label>
              <CustomSearchableDropdown
                items={repeatOptionsList}
                value={repeatType}
                labelKey="Label"
                valueKey="Value"
                searchable={false}
                onChange={(val) => {
                  setRepeatType(val);
                  setWeeklyOptions([]);
                  setMonthlyOption(null);
                  setEndDate(null);
                  setIsRepeatEnabled(false);
                  setErrors((prev) => ({ ...prev, endDate: "" }));
                }}
              />
            </div>

            {/* Weekly */}
            {(repeatType === 2 || repeatType === 4) && (
              <div className="mb-4">
                <div className="d-flex flex-wrap gap-2">
                  {weeklyOptionsList.map((day) => (
                    <button
                      type="button"
                      key={day.Value}
                      className={`repeat-day-btn ${weeklyOptions.includes(day.Value) ? "active" : ""
                        }`}
                      onClick={() => {
                        setWeeklyOptions((prev) =>
                          prev.includes(day.Value)
                            ? prev.filter(d => d !== day.Value)
                            : [...prev, day.Value]
                        );
                        setErrors((prev) => ({
                          ...prev,
                          recurrenceDay: ""
                        }));
                      }}
                    >
                      {day.Label}
                    </button>
                  ))}
                </div>
                {errors.recurrenceDay && (
                  <div className="error-text">
                    <img src={Images.ErrorMsg}></img> <p>{errors.recurrenceDay}</p>
                  </div>
                )}
              </div>
            )}

            {/* Monthly */}
            {repeatType === 3 && (
              <div className="mb-4">
                <CustomSearchableDropdown
                  items={monthlyOptionsList}
                  value={monthlyOption}
                  labelKey="Label"
                  valueKey="Value"
                  searchable={false}
                  onChange={(val) => setMonthlyOption(val)}
                />
              </div>
            )}

            {/* End Date */}
            {repeatType !== 0 && (
              <>
                <div className="form-check form-switch require-booking-sec d-inline-flex align-items-center">
                  <label className="popup-inner-title mt-0 text-start p-0 flex-unset mb-0 fw-bold">{t("EndDate")}

                  </label>
                  <span
                    className="ms-2 me-3 position-relative"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    style={{ cursor: "pointer" }}
                  >
                    <img src={Images.TooltipIcon} alt="tooltip" />

                    {showTooltip && (

                      <div className="custom-tooltip-meeting position-absolute">
                        <div className="tooltip-title d-flex align-items-center gap-2">
                          <img src={Images.InfoIcon} className="img-fluid" />
                          <h6>{t("end_date")}</h6>
                        </div>
                        <div className="tooltip-desc">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <img src={Images.GreenToggle} className="img-fluid" />
                            <p>
                              <Trans
                                i18nKey="react_to_set_end_date_in_meeting"
                                components={{ 1: <span className="text-green" /> }}
                              />
                            </p>

                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <img src={Images.GrayToggle} className="img-fluid" />
                            <p>
                              <Trans
                                i18nKey="react_to_set_recurring_meeting"
                                components={{ 1: <span className="text-green" /> }}
                              />
                              {/* Slide to the <span className="zone_text">left</span> to keep the meeting recurring indefinitely. */}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </span>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="calendar-sync"
                    name="syncCalendar"
                    checked={isRepeatEnabled}
                    onChange={(e) => {
                      setIsRepeatEnabled(e.target.checked);
                      if (!e.target.checked) setEndDate(null);
                      setErrors((prev) => ({
                        ...prev,
                        endDate: e.target.checked ? prev.endDate : ""
                      }));
                    }}
                  />
                  {/*  <input
                    className="form-check-input"
                    type="checkbox"

                    checked={isRepeatEnabled}
                    onChange={(e) => {
                      setIsRepeatEnabled(e.target.checked);
                      if (!e.target.checked) setEndDate(null);
                    }}
                  /> */}
                </div>

                <div className={`date-picker-wrapper mb-4 ${!isRepeatEnabled ? 'disable-date' : ''}`}
                >
                  <CustomDatePicker
                    autoComplete="off"
                    selected={endDate}
                    onChange={(date) => {
                      setEndDate(date);
                      setErrors((prev) => ({ ...prev, endDate: "" }));
                    }}
                    disabled={!isRepeatEnabled}
                    minDate={selectedDate}
                    placeholderText={isRepeatEnabled ? "--" : t("indefinitely")}
                  />
                </div>
                {errors.endDate && (
                  <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.endDate}</p></div>
                )}
              </>
            )}

            {/* Notes */}
            <div className="mb-4">
              <label className="popup-inner-title">{t("description")}</label>
              <textarea
                className="form-control"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

          </form>
        </div>
      )}

      {/* ================= STEP 2 ================= */}
      {/* STEP 2 */}
      {(step === 1 && currentSection === 1) && (
        <div className="step active" data-step="1">

          <form>
            {/* Space */}
            <div className=" mb-4">
              <label className="popup-inner-title mt-0">{t("Room")}</label>
              <CustomSearchableDropdownTickmark
                items={spotList}
                value={selectedSpot}
                labelKey="SpotName"
                valueKey="Id"
                searchable={false}
                disabled={spotList.length === 0}
                placeholder={
                  spotList.length === 0
                    ? t("no_space_available")
                    : t("select") + " " + t("space")
                }
                onChange={(value) => {
                  setSelectedSpot(value);
                  const selectedSpotObj = spotList.find(
                    (item) => item.Id === value
                  );

                  console.log("Selected Spot:", selectedSpot);
                  console.log("Selected Spot Obj:", selectedSpotObj);
                  if (selectedSpotObj) {
                    setIsRoomHasApprover(selectedSpotObj.IsRoomHasApprover || 0);

                    // ⚠️ Not available in your API per spot → keep 0
                    setIsFullyDayBlocked(selectedSpotObj.IsFullyDayBlocked || 0);
                    setCapacity(selectedSpotObj.Capacity || 0);
                  }
                }}

              />

              {/* <img src={Images.RollIconGray} alt="" className="map-icon" /> */}
            </div>
            {isStep2NoSpaceAvailable && (
              <div className="no-space-found text-center mt-3">

                <img
                  src={Images.NoSpaceError}
                  className="img-fluid mb-2"
                  style={{ maxWidth: "160px" }}
                />

                <p className="text-red fs-5">
                  {t("react_new_booking_no_available_spaces")}
                </p>

                <p className="">
                  {t("react_new_booking_please_update_selection")}
                </p>

              </div>
            )}

            {spotList.length > 0 && timeSelectionSource !== "step1" && (

              <div className="custom-timepicker mt-3">

                <label className="popup-inner-title">
                  {t("react_select_time")}
                </label>

                <div
                  key={selectedSpot}
                  className={`slot-container d-grid mb-3 ${errors.timeInvalid ? "error-form" : ""}`}

                >

                  {timeSlots.map((slot, idx) => {

                    const [start, end] = slot.split("-");
                    const isDisabled = isStep2MeetingSlotDisabled(slot);
                    const isSelected = isMeetingSlotWithinSelectedRange(start, end);

                    return (
                      <div
                        key={idx}
                        className={`slot ${isDisabled ? "disabled" : ""} ${isSelected ? "active" : ""}`}
                        onClick={() => {
                          if (!isDisabled) {
                            handleStep2MeetingSlotClick(slot);
                          }
                        }}
                      >
                        {slot}
                      </div>
                    );
                  })}

                </div>
                {errors.timeInvalid && (

                  <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.timeInvalid}</p></div>
                )}
              </div>
            )}

          </form>

        </div>
      )}

      {/* STEP 3 */}
      {(step === 2 && currentSection === 2) && (
        <div className="step active" data-step="2">
          <form>
            <div className=" mb-4">
              <label className="popup-inner-title mt-0">{t("add_employee")}</label>

              <CustomSearchableDropdown
                items={employeeOptions || []}
                value={employee}  // string
                labelKey="label"
                valueKey="value"
                searchable={true}
                placeholder={t("name")}
                onChange={(empId) => {  // empId is string
                  //console.log("Selected employee ID:", empId);
                  setEmployee(empId);    // store string ID
                  addEmployeeById(empId);
                }}
              />

              {!showGuestForm && errors.capacity && (
                <div className="error-text">
                  <img src={Images.ErrorMsg} alt="" />
                  <p>
                    {errors.capacity}
                  </p>
                </div>
              )}
            </div>
            <div className="modal-btn my-4">
              <a
                className="green-filled-with-icon"
                role="button"
                onClick={() => setShowGuestForm(prev => !prev)}
              >
                <span><img src={Images.GreenPlus} /></span>
                {t("react_add_external_guest_at_new_booking")}
              </a>

              {showGuestForm && (
                <div className="card border-0 mt-3 ">
                  <div className="form-row mb-4">
                    <label className="popup-inner-title m-0" htmlFor="name">{t("firstName") + "*"}</label>
                    <input
                      type="text"
                      //className={`form-control ${errors.firstname ? "error-form" : ""}`}
                      className={`form-control 
                      ${errors.firstname ? "error-form" : ""}
                      ${!errors.firstname && guestInput.name ? "success-validation" : ""}
                      `}
                      autoComplete="off"
                      placeholder={t("firstName")}
                      value={guestInput.name}
                      onChange={(e) => {
                        setGuestInput({ ...guestInput, name: e.target.value });
                        setErrors((prev) => ({ ...prev, firstname: "" }));
                      }}
                    />

                    {errors.firstname && (

                      <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.firstname}</p></div>
                    )}
                  </div>
                  <div className="form-row mb-4">
                    <label className="popup-inner-title m-0">{t("lastName") + "*"}</label>
                    <input
                      type="text"
                      //className={`form-control ${errors.lastname ? "error-form" : ""}`}
                      className={`form-control 
                      ${errors.lastname ? "error-form" : ""}
                      ${!errors.lastname && guestInput.surname ? "success-validation" : ""}
                      `}
                      autoComplete="off"
                      placeholder={t("lastName")}
                      value={guestInput.surname}
                      onChange={(e) => {
                        setGuestInput({ ...guestInput, surname: e.target.value });
                        setErrors((prev) => ({ ...prev, lastname: "" }));
                      }}
                    />
                    {errors.lastname && (

                      <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.lastname}</p></div>
                    )}
                  </div>
                  <div className="form-row mb-4">
                    <label className="popup-inner-title m-0">{t("email") + "*"}</label>
                    <div className="position-relative">
                    <input
                      type="email"
                      className={`form-control 
                              ${errors.email ? "error-form" : ""}
                              ${!errors.email && guestInput.email ? "success-validation" : ""}
                              `}
                      placeholder={t("email")}
                      value={guestInput.email}
                      onChange={(e) => {
                        const value = e.target.value;

                        setGuestInput({ ...guestInput, email: value });

                        // ✅ Remove error immediately when user types
                        setErrors((prev) => ({ ...prev, email: "" }));
                      }}
                    />

                    <img
                      src={Images.EmailMessage}
                      // Hidden while the input shows its own right-side icon - the red error icon
                      // (error-form) or the green success tick (success-validation) - so they don't overlap
                      className={`form-icon ${errors.email || guestInput.email ? "d-none" : ""}`}
                    />
                    {errors.email && (

                      <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.email}</p></div>
                    )}
                    </div>
                  </div>
                  <div className="form-row mb-4">
                    <label className="popup-inner-title m-0" htmlFor="name">{t("national_id")} ({t("optional")})</label>
                    <input
                      type="text"
                      className="form-control"
                      autoComplete="off"
                      placeholder={t("national_id") + " (" + t("optional") + ")"}
                      value={guestInput.nationalIdentificationId}
                      onChange={(e) => setGuestInput({ ...guestInput, nationalIdentificationId: e.target.value })}
                    />
                  </div>
                  {errors.capacity && (
                    <div className="error-text">
                      <img src={Images.ErrorMsg}></img>
                      <p>{errors.capacity}</p>
                    </div>
                  )}
                  <div className="modal-btn">
                    <a
                      href="#"
                      className="main-btn outline-green-btn mb-3"
                      onClick={handleConfirmExternalGuest}
                    >
                      {t("react_confirm_external_guest_at_new_booking")}
                    </a>

                    <a href="#collapseExample" role="button" aria-expanded="false" aria-controls="collapseExample" className="main-btn outline-gray-btn"
                      onClick={() => setShowGuestForm(false)} >
                      <img src={Images.ArrowsVerticalExpand} className="img-fluid" />{t("react_collapse_section_at_new_booking")}</a                           >
                  </div>

                </div>
              )}
            </div>

            {/*------------------All Participants-list------------------*/}
            <div className="form-row participants-list ">
              <label className="popup-inner-title m-0" >{t("participants")}</label>
              <div className="form-check bg-white">

                <label className="form-check-label first-check-label">
                  {FirstName} {LastName} ({memberEmail})
                </label>
              </div>
              {selectedEmployees.length > 0 &&
                selectedEmployees.map((emp) => (
                  <div key={emp.EmployeeId}
                    //className="form-check d-flex align-items-center bg-white"
                    className={`form-check d-flex align-items-center bg-white ${checkedEmployees.includes(emp.EmployeeId) ? "check-active" : ""}`}
                  >
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`emp-${emp.EmployeeId}`}
                      checked={checkedEmployees.includes(emp.EmployeeId)}
                      onChange={() => handleCheckboxChange(emp.EmployeeId)}
                    />

                    <label
                      className="form-check-label ms-2"
                      htmlFor={`emp-${emp.EmployeeId}`}
                    >
                      {emp.EmployeeEmail
                        ? `${emp.EmployeeName} (${emp.EmployeeEmail})`
                        : emp.EmployeeName}
                    </label>

                    <a
                      className={`btn btn-link p-0 ms-2 check-delete ${checkedEmployees.includes(emp.EmployeeId) ? "opacity-1" : "opacity-0"}`}
                      //className="btn btn-link p-0 ms-2 check-delete"
                      onClick={() => handleRemoveEmployee(emp.EmployeeId)}
                      disabled={!checkedEmployees.includes(emp.EmployeeId)}
                      aria-label={`Remove ${emp.EmployeeName}`}
                    >
                      <img src={Images.Trash} className="img-fluid" alt="delete" />
                    </a>
                  </div>
                ))}

              {Array.isArray(participants) && participants.length > 0 &&
                participants.map((p) => (
                  <div
                    className={`form-check form-active d-flex align-items-center bg-white ${checkedParticipants.includes(p.id) ? "check-active" : ""
                      }`}
                    key={p.id}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`participant-${p.id}`}
                      checked={checkedParticipants.includes(p.id)}
                      onChange={() => handleParticipantCheckboxChange(p.id)}
                    />

                    <label
                      className="form-check-label ms-2"
                      htmlFor={`participant-${p.id}`}
                    >
                      {p.name} ({p.email})
                    </label>

                    <a
                      type="button"
                      className={`btn btn-link p-0 ms-2 check-delete ${checkedParticipants.includes(p.id) ? "opacity-1" : "opacity-0"}`}
                      onClick={() => handleRemoveParticipant(p.id)}
                      disabled={!checkedParticipants.includes(p.id)} // UX improvement
                      aria-label={`Remove ${p.name}`}
                    >
                      <img src={Images.Trash} className="img-fluid" alt="delete" />
                    </a>
                  </div>
                ))}

            </div>
          </form>
        </div>
      )}

      {/* STEP 4 */}
      {currentSection === 3 && (
        <div className="step active" data-step="3">

          <div className="modal-des">

            <div className="icon-with-list mb-0">
              <div className="icon-wrap mb-3">
                <img src={Images.Participant} className="img-fluid" alt="organizer" />
                <h4>{FirstName} {LastName}</h4>
              </div>

              <div className="icon-wrap mb-3">
                <img src={Images.GreenCalender} className="img-fluid" alt="date" />
                <h4>
                  {displaySelectedDate}
                </h4>

              </div>

              <div className="icon-wrap mb-3">
                <img src={Images.ZoneMap ?? Images.GreenCalender} className="img-fluid" alt="spot" />
                <h4>
                  {selectedSpotObj?.SpotName}
                  <span className="zone_text"> {meetingName}</span>
                </h4>
              </div>

              <div className="icon-wrap mb-3">
                <img src={Images.TimeIcon} className="img-fluid" alt="time" />
                <h4>
                  {t("from")} <span className="zone_text">{selectedFrom || "00:00"}</span> {t("react_to")} <span className="zone_text">{selectedTo || "00:00"}</span>
                </h4>
              </div>

              <div className="icon-wrap mb-5">
                <img src={Images.Participant} className="img-fluid" alt="participants" />
                <h4>
                  {`${1 + (participants?.length || 0) + (selectedEmployees?.length || 0)} ${t(
                    1 + (participants?.length || 0) + (selectedEmployees?.length || 0) === 1
                      ? "react_create_meeting_participant"
                      : "react_create_meeting_participants"
                  )}`}
                </h4>
              </div>
            </div>
            <div className="modal_img text-center pb-4">
              <img src={Images.Meeting} className="img-fluid" alt="meeting" />
            </div>
          </div>
        </div>
      )}
      {/* STEP 4 */}

      {needApproval && currentSection === 4 && (
        <div className="step active" data-step="4">
          <div className="modal-des">
            <div className="icon-with-list mb-0">
              {/* APPROVER */}
              <div className="icon-wrap mb-3">
                <img src={Images.Participant} className="img-fluid" />
                <h4>
                  <span className="zone_text">
                    {t(
                      approverList.join(", ").includes(",")
                        ? "react_create_meeting_approvers"
                        : "react_create_meeting_approver"
                    ).split("%s")[0]}
                  </span>
                  {approverList.join(", ")}
                  <span className="zone_text">
                    {t(
                      approverList.join(", ").includes(",")
                        ? "react_create_meeting_approvers"
                        : "react_create_meeting_approver"
                    ).split("%s")[1] || ""}
                  </span>
                </h4>
              </div>
              {/* DATE */}
              <div className="icon-wrap mb-3">
                <img src={Images.GreenCalender} className="img-fluid" />
                <h4>
                  {displaySelectedDate}</h4>
              </div>
              {/* APPROVAL INFO */}
              <div className="icon-wrap d-flex align-items-start">
                <img src={Images.TimeIcon} className="img-fluid mt-1" />
                {/* <h4><span className="zone_text d-block mb-1">Requests must be made at least 02 hours before start time. </span>An unapproved request will be released 01 hour before start time</h4> */}
                {/* <h4><span className="zone_text d-block mb-1"></span>{approvalInfo}</h4> */}
                <h4>
                  <span className="zone_text d-block mb-1">
                    {/* Requests must be made at least {requestWindow.hours} hours, {requestWindow.minutes} minutes before start time. */}
                    <span className="zone_text d-block mb-1">
                      {formatReactString(
                        t("meeting_room_requires_approval_from_assigned_approver_heading2"),
                        [requestWindow.hours, requestWindow.minutes]
                      )}
                    </span>
                    <span className="zone_text d-block mb-1" style={{ color: "#000" }}>

                      {formatReactString(
                        t("meeting_room_requires_approval_from_assigned_approver_heading3"),
                        [releaseWindow.hours, releaseWindow.minutes]
                      )}
                    </span>
                  </span>


                </h4>

              </div>
            </div>
            <div className="modal_img text-center mb-3 pb-1"><img src={Images.Unapproved} className="img-fluid" /></div>
          </div>
        </div>
      )}
      {/* Footer Buttons */}

      <div
        className={`actions d-flex ${(currentSection < 5 && currentSection > 0)
          ? "justify-content-between"
          : "justify-content-end"
          }  mt-3 `}
      >

        {/* BACK BUTTON */}
        {/* {currentSection > 0 && currentSection < 3 && ( */}
        {(currentSection < 5 && currentSection > 0) && (
          /* {(currentSection < 5) && ( */
          <button
            type="button"
            //className="main-btn outline-gray-btn"
            className={`main-btn outline-gray-btn ${currentSection === 4 ? "disable pe-auto" : ""}`}
            onClick={handleBack}
            disabled={currentSection === 4}
          >
            {t("back")}
          </button>
        )}


        {/* NEXT BUTTON */}

        {currentSection < 3 && (
          <button
            type="button"
            className={`main-btn fill-green-btn ${isStep2NextDisabled ? "green-disabled" : ""}`}
            onClick={handleNext}
            id="nextBtn"
            disabled={isStep2NextDisabled}
          >
            {t("next")}
          </button>
        )}

        {/* CONFIRM BUTTON */}
        {(currentSection === 3 || currentSection === 4) && (
          <button
            type="button"
            className="main-btn fill-green-btn"
            disabled={isLoading}
            //onClick={handleConfirm}
            onClick={() => {
              if (isLoading) return; // ✅ prevent click
              if (currentSection === 4) {
                history.push("/booking");
              } else {
                //if (!isLoading) {
                handleConfirm();
                //}
              }
            }}
          >
            {t("ok")}
          </button>
        )}

      </div>
      {(notification) && (
        <NotificationMessage
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      {renderApiUi(isLoading)}
    </div>

  );
};

export default BookMeeting;
